import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";
import { useState, useEffect } from "react";
import { GoPerson } from "react-icons/go";
import { RiLockPasswordLine } from "react-icons/ri";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { api1 } from "../../../utils/axiosInstance";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import { setSubscription } from "../../../redux/authSlice";
import { API_ENDPOINTS } from "../../../services/apiEndpoints";
import { SubscriptionPlans } from "../../../components/SubscriptionPlans";
import useGoogleAuth from "../useGoogleAuth";

const SignupForm = () => {
  const location = useLocation();
  const { handleGoogleAuth } = useGoogleAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [termsError, setTermsError] = useState("");

  useEffect(() => {
    const isFromGoogle = location.state?.fromGoogle || localStorage.getItem("isFromGoogle") === "true";
    const firstName = localStorage.getItem("firstName");
    const lastName = localStorage.getItem("lastName");
    const email = localStorage.getItem("email");

    setUser({
      firstName: firstName || "",
      lastName: lastName || "",
      email: email || "",
    });

    setCurrentStep(isFromGoogle && firstName ? 2 : 1);
  }, [location.state?.fromGoogle]);

  const isPasswordValid = (password) => {
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);

    return password.length >= minLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
  };

  const isFormValid = () => {
    const passwordMatch = user.password === user.confirmPassword;
    return user.firstName && user.lastName && user.email && user.password && user.confirmPassword && passwordMatch && isPasswordValid(user.password);
  };

  const proceedToSubscription = (e) => {
    e.preventDefault();
    setTermsError("");

    if (!isFormValid()) {
      const passwordMatch = user.password === user.confirmPassword;
      if (!passwordMatch) {
        toast.error("Password and confirm password do not match");
      } else if (!isPasswordValid(user.password)) {
        toast.error("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
      } else {
        toast.error("Please fill in all required fields");
      }
      return;
    }

    if (!isChecked) {
      setTermsError("You must accept the terms to proceed.");
      return;
    }

    setCurrentStep(2);
  };

  const handleSubscription = async (plan) => {
    const type = localStorage.getItem("userType");
    const isFromGoogle = localStorage.getItem("isFromGoogle") === "true";
    const userData = { ...user, type, subscriptionType: plan, isGoogleUser: isFromGoogle };

    try {
      if (isFromGoogle) {
        const response = await api1.post(API_ENDPOINTS.UPDATE_GOOGLE_USER_PLAN, userData);
      
        if (response.data.user && response.data.user.subscription) {
          dispatch(setSubscription(response.data.user.subscription));
        }
        
        localStorage.removeItem("isFromGoogle");
        
        if (plan === "free") {
          navigate("/dashboard/profile");
        } else {
          const { data } = await api1.post(API_ENDPOINTS.CREATE_STRIPE_SESSION, {
            ...userData,
            userId: response.data.user.id
          });
          window.location.href = data.checkoutUrl;
        }
        return;
      }

      if (plan === "free") {
        const response = await api1.post(API_ENDPOINTS.SIGNUP, userData);
        localStorage.setItem("userEmail", response.data.email);
        localStorage.setItem("firstName", response.data.firstName);
        localStorage.setItem("lastName", response.data.lastName);
        navigate("/auth/otp", { state: { from: "/auth/signup" } });
      } else {
        const pendingRes = await api1.post(API_ENDPOINTS.SAVE_PENDING_USER, userData);
        const { data } = await api1.post(API_ENDPOINTS.CREATE_STRIPE_SESSION, {
          pendingUserId: pendingRes.data.pendingUserId,
          subscriptionType: plan,
          email: user.email,
        });
        window.location.href = data.checkoutUrl;
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error(isFromGoogle ? "Google plan setup failed." : "Subscription setup failed. Please try again.");
    }
  };

  const handlePlanSelect = async (plan) => {
    setSelectedSubscription(plan);
    await handleSubscription(plan);
  };

  const updateUserData = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  return (
    <div className="w-full flex flex-col items-center justify-center p-4 md:p-8 bg-white overflow-y-auto">
      {currentStep === 1 ? (
        <div>
          <h1 className="text-3xl font-bold text-purple-800 mb-2">Create your account</h1>
          <p className="text-gray-500 mb-6">Welcome to OnTrack Careers. Let's get started by creating your account.</p>

          <form className="w-full max-w-md space-y-4">
            <InputField name="firstName" placeholder="Enter your first name" icon={<GoPerson />} onChange={updateUserData} />
            <InputField name="lastName" placeholder="Enter your last name" icon={<GoPerson />} onChange={updateUserData} />
            <InputField name="email" type="email" placeholder="Enter your email" icon={<GoPerson />} onChange={updateUserData} />

            <PasswordField name="password" placeholder="Enter your password" show={showPassword} toggle={() => setShowPassword(!showPassword)} onChange={updateUserData} />
            <PasswordField name="confirmPassword" placeholder="Confirm your password" show={showConfirmPassword} toggle={() => setShowConfirmPassword(!showConfirmPassword)} onChange={updateUserData} />

            <div className="text-sm text-gray-600 leading-relaxed">
              <p>
                By subscribing to OnTrack Careers, you confirm that you have <strong>read, understood, and agreed</strong> to our{" "}
                <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>. These documents outline your rights and responsibilities, how we handle your data, and the rules governing your use of our platform.
              </p>

              <p className="mt-3">We encourage you to review them carefully:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-purple-600">
                <li><a href="/termsOfService" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a></li>
                <li><a href="/privacyPolicy" target="_blank" rel="noopener noreferrer" className="underline">View Privacy Policy</a></li>
              </ul>
            </div>

            <div className="flex items-start gap-2 mt-4">
              <input
                type="checkbox"
                id="terms"
                checked={isChecked}
                onChange={(e) => {
                  setIsChecked(e.target.checked);
                  setTermsError("");
                }}
                className="mt-1"
              />
              <label htmlFor="terms" className="text-sm text-gray-600 leading-snug">
                By proceeding, you acknowledge and accept these policies in full.
              </label>
            </div>

            {termsError && <p className="text-red-500 text-xs mt-1 ml-6">{termsError}</p>}

            <button type="button" onClick={proceedToSubscription} className="w-full py-3 text-white bg-custom-gradient rounded-lg shadow-md hover:opacity-90 transition-all">
              Continue
            </button>

            <div className="flex items-center my-4">
              <hr className="flex-grow border-gray-300" />
              <p className="text-gray-500 px-4">Or</p>
              <hr className="flex-grow border-gray-300" />
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={(res) =>
                  handleGoogleAuth({
                    credentialResponse: res,
                    userType: localStorage.getItem("userType"),
                    dispatch,
                    setUser,
                    setCurrentStep,
                  })
                }
                onError={() => console.error("Google login failed")}
                theme="outline"
                size="large"
                shape="rectangular"
                width="100%"
              />
            </div>
          </form>

          <p className="text-sm text-center text-gray-500 mt-4">
            Already have an account?{" "}
            <span onClick={() => navigate("/auth/login")} className="text-purple-600 cursor-pointer hover:underline">
              Login
            </span>
          </p>
        </div>
      ) : (
        <>
          <h1 className="text-3xl font-bold text-purple-800 mb-2">Choose Your Plan</h1>
          <p className="text-gray-500 mb-6">Select a subscription plan that works best for you</p>

          <SubscriptionPlans
            variant="subscription"
            selectedPlan={selectedSubscription}
            onPlanSelect={handlePlanSelect}
            columns={1}
          />

          <div className="flex w-full max-w-md mt-8 gap-4">
            <button
              onClick={() => setCurrentStep(1)}
              className="w-full py-3 text-purple-600 bg-white border border-purple-600 rounded-lg hover:bg-purple-50 transition-all"
            >
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const InputField = ({ name, placeholder, icon, type = "text", onChange }) => (
  <div>
    <div className="flex items-center border border-gray-300 rounded px-3 py-2">
      {icon}
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        className="w-full outline-none text-gray-700 placeholder:text-sm ml-2"
        onChange={onChange}
      />
    </div>
  </div>
);

const PasswordField = ({ name, placeholder, show, toggle, onChange }) => (
  <div className="relative">
    <div className="flex items-center border border-gray-300 rounded px-3 py-2">
      <RiLockPasswordLine className="mr-2 text-gray-500" />
      <input
        name={name}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        className="w-full outline-none text-gray-700 placeholder:text-sm"
        onChange={onChange}
      />
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer" onClick={toggle}>
        {show ? <EyeOffIcon className="w-5 h-5 text-gray-500" /> : <EyeIcon className="w-5 h-5 text-gray-500" />}
      </div>
    </div>
  </div>
);

export default SignupForm;
